import type { IoTDataPoint } from '../types/realtime.types';

/**
 * Telemetry is considered stale when the station is still `online` but `lastUpdated` is older than this window.
 * Can be moved to env later if operators need a different SLA.
 */
export const STALE_TELEMETRY_MS = 15 * 60 * 1000;

export interface IoTOperationalSummary {
  total: number;
  online: number;
  offline: number;
  /** Online stations whose `lastUpdated` is older than {@link STALE_TELEMETRY_MS}. */
  staleCount: number;
  normal: number;
  warning: number;
  danger: number;
}

function thresholdBucket(s: IoTDataPoint): 'normal' | 'warning' | 'danger' {
  if (!s.thresholdBreached) return 'normal';
  if (s.thresholdSeverity === 'warning') return 'warning';
  return 'danger';
}

/** Aggregate operational counters in a single pass over `stations`. */
export function summarizeIoTStations(stations: IoTDataPoint[], nowMs: number): IoTOperationalSummary {
  let online = 0;
  let offline = 0;
  let staleCount = 0;
  let normal = 0;
  let warning = 0;
  let danger = 0;

  for (const s of stations) {
    if (s.status === 'offline') offline += 1;
    else online += 1;

    if (s.status === 'online' && nowMs - s.lastUpdated > STALE_TELEMETRY_MS) {
      staleCount += 1;
    }

    const b = thresholdBucket(s);
    if (b === 'normal') normal += 1;
    else if (b === 'warning') warning += 1;
    else danger += 1;
  }

  return {
    total: stations.length,
    online,
    offline,
    staleCount,
    normal,
    warning,
    danger,
  };
}

export function isStationTelemetryStale(s: IoTDataPoint, nowMs: number): boolean {
  return s.status === 'online' && nowMs - s.lastUpdated > STALE_TELEMETRY_MS;
}

/**
 * Relative time vs now (past/future) using the active i18n language tag.
 */
export function formatRelativeStationTime(timestampMs: number, i18nLanguage: string): string {
  if (!Number.isFinite(timestampMs)) return '—';
  const diffSec = Math.round((timestampMs - Date.now()) / 1000);
  const abs = Math.abs(diffSec);
  let unit: Intl.RelativeTimeFormatUnit;
  let value: number;
  if (abs < 60) {
    unit = 'second';
    value = diffSec;
  } else if (abs < 3600) {
    unit = 'minute';
    value = Math.round(diffSec / 60);
  } else if (abs < 86400) {
    unit = 'hour';
    value = Math.round(diffSec / 3600);
  } else {
    unit = 'day';
    value = Math.round(diffSec / 86400);
  }
  const localeTag = i18nLanguage.toLowerCase().startsWith('vi') ? 'vi-VN' : 'en-US';
  return new Intl.RelativeTimeFormat(localeTag, { numeric: 'auto' }).format(value, unit);
}
